import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Save, Star, BarChart3, Shield, Award, Globe } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧', bg: 'bg-blue-50', text: 'text-blue-800' },
  { code: 'bg', name: 'Български', flag: '🇧🇬', bg: 'bg-green-50', text: 'text-green-800' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', bg: 'bg-yellow-50', text: 'text-yellow-800' }
];

const DEFAULT_BENEFITS = [
  { title_en: 'Fully Insured', title_bg: 'Пълна застраховка', title_de: 'Vollversichert', description_en: 'All shipments are covered by comprehensive cargo insurance.', description_bg: 'Всички пратки са покрити с цялостна застраховка на товара.', description_de: 'Alle Sendungen sind durch umfassende Frachtversicherung abgedeckt.', highlight_en: '100% Coverage', highlight_bg: '100% покритие', highlight_de: '100% Deckung' },
  { title_en: 'Modern Fleet', title_bg: 'Модерен флот', title_de: 'Moderne Flotte', description_en: 'EURO 6 compliant vehicles with ECO zone access.', description_bg: 'EURO 6 превозни средства с достъп до ECO зони.', description_de: 'EURO 6-konforme Fahrzeuge mit Zugang zu ECO-Zonen.', highlight_en: 'EURO 6 Standard', highlight_bg: 'EURO 6 стандарт', highlight_de: 'EURO 6 Standard' },
  { title_en: 'GPS Tracking', title_bg: 'GPS проследяване', title_de: 'GPS-Verfolgung', description_en: 'Real-time tracking on every shipment.', description_bg: 'Проследяване в реално време на всяка пратка.', description_de: 'Echtzeit-Tracking für jede Sendung.', highlight_en: 'Live Updates', highlight_bg: 'На живо', highlight_de: 'Live-Updates' },
  { title_en: 'Express Delivery', title_bg: 'Експресна доставка', title_de: 'Expresslieferung', description_en: 'Time-critical shipments delivered within 24-40 hours.', description_bg: 'Времево критични пратки доставени в рамките на 24-40 часа.', description_de: 'Zeitkritische Sendungen innerhalb von 24-40 Stunden geliefert.', highlight_en: '24-40 Hours', highlight_bg: '24-40 часа', highlight_de: '24-40 Stunden' },
  { title_en: '24/7 Support', title_bg: '24/7 поддръжка', title_de: '24/7 Support', description_en: 'Our dedicated team is available around the clock.', description_bg: 'Нашият екип е на разположение денонощно.', description_de: 'Unser Team ist rund um die Uhr verfügbar.', highlight_en: 'Always Available', highlight_bg: 'Винаги на линия', highlight_de: 'Immer erreichbar' },
  { title_en: 'Licensed Carrier', title_bg: 'Лицензиран превозвач', title_de: 'Lizenzierter Spediteur', description_en: 'Fully licensed EU carrier and IRU member.', description_bg: 'Напълно лицензиран EU превозвач и член на IRU.', description_de: 'Voll lizenzierter EU-Spediteur und IRU-Mitglied.', highlight_en: 'IRU Member', highlight_bg: 'IRU член', highlight_de: 'IRU-Mitglied' }
];

const DEFAULT_GRID_ITEMS = [
  { title_en: 'EU Wide', title_bg: 'EU покритие', title_de: 'EU-weit', sub_en: 'Coverage', sub_bg: 'Широко', sub_de: 'Abdeckung' },
  { title_en: 'ECO', title_bg: 'ECO', title_de: 'ECO', sub_en: 'Friendly Fleet', sub_bg: 'Екологичен флот', sub_de: 'Freundliche Flotte' },
  { title_en: 'Secure', title_bg: 'Сигурен', title_de: 'Sicher', sub_en: 'Transport', sub_bg: 'Транспорт', sub_de: 'Transport' },
  { title_en: 'Expert', title_bg: 'Експертен', title_de: 'Experten', sub_en: 'Team', sub_bg: 'Екип', sub_de: 'Team' }
];

export default function WhyChooseUsManager() {
  const [content, setContent] = useState({
    hero_subtitle_en: 'WHY CHOOSE US', hero_subtitle_bg: 'ЗАЩО ДА ИЗБЕРЕТЕ НАС', hero_subtitle_de: 'WARUM UNS WÄHLEN',
    hero_title_en: 'Your Trusted', hero_title_bg: 'Вашият доверен', hero_title_de: 'Ihr vertrauenswürdiger',
    hero_title_highlight_en: 'Transport Partner', hero_title_highlight_bg: 'транспортен партньор', hero_title_highlight_de: 'Transportpartner',
    hero_description_en: '', hero_description_bg: '', hero_description_de: '',
    stats: [
      { number: '20+', label_en: 'Years Experience', label_bg: 'Години опит', label_de: 'Jahre Erfahrung' },
      { number: '50,000+', label_en: 'Shipments Delivered', label_bg: 'Доставени пратки', label_de: 'Lieferungen' },
      { number: '99.5%', label_en: 'On-Time Rate', label_bg: 'Навременност', label_de: 'Pünktlichkeitsrate' },
      { number: '28+', label_en: 'Countries Served', label_bg: 'Обслужени държави', label_de: 'Länder bedient' }
    ],
    benefits_subtitle_en: 'OUR ADVANTAGES', benefits_subtitle_bg: 'НАШИТЕ ПРЕДИМСТВА', benefits_subtitle_de: 'UNSERE VORTEILE',
    benefits_title_en: 'What Sets Us', benefits_title_bg: 'Какво ни прави', benefits_title_de: 'Was uns',
    benefits_title_highlight_en: 'Apart', benefits_title_highlight_bg: 'различни', benefits_title_highlight_de: 'unterscheidet',
    benefits: DEFAULT_BENEFITS,
    guarantees_subtitle_en: 'QUALITY ASSURANCE', guarantees_subtitle_bg: 'ГАРАНЦИЯ ЗА КАЧЕСТВО', guarantees_subtitle_de: 'QUALITÄTSSICHERUNG',
    guarantees_title_en: 'Our', guarantees_title_bg: 'Нашите', guarantees_title_de: 'Unsere',
    guarantees_title_highlight_en: 'Guarantees', guarantees_title_highlight_bg: 'гаранции', guarantees_title_highlight_de: 'Garantien',
    guarantees_text_en: '', guarantees_text_bg: '', guarantees_text_de: '',
    guarantees_en: ['On-time delivery or money back guarantee', 'Professional handling of all cargo types', 'Transparent pricing with no hidden fees', 'Regular updates throughout transport', 'Flexible scheduling options', 'Customs clearance assistance'],
    guarantees_bg: ['Навременна доставка или връщане на парите', 'Професионално боравене с всички видове товари', 'Прозрачно ценообразуване без скрити такси', 'Редовни актуализации по време на транспорта', 'Гъвкави опции за планиране', 'Помощ при митническо оформяне'],
    guarantees_de: ['Pünktliche Lieferung oder Geld zurück', 'Professionelle Handhabung aller Frachtarten', 'Transparente Preisgestaltung ohne versteckte Gebühren', 'Regelmäßige Updates während des Transports', 'Flexible Terminoptionen', 'Zollabfertigungshilfe'],
    cta_title_en: 'Ready to Experience the Difference?', cta_title_bg: 'Готови да изпитате разликата?', cta_title_de: 'Bereit, den Unterschied zu erleben?',
    cta_text_en: '', cta_text_bg: '', cta_text_de: '',
    get_quote_btn_en: 'Get a Free Quote', get_quote_btn_bg: 'Безплатна оферта', get_quote_btn_de: 'Kostenloses Angebot',
    contact_btn_en: 'Contact Us', contact_btn_bg: 'Свържете се с нас', contact_btn_de: 'Kontaktieren Sie uns',
    grid_items: DEFAULT_GRID_ITEMS
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeLang, setActiveLang] = useState('en');

  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/admin/why-choose-us-content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching why choose us content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/why-choose-us-content`, content);
      toast.success('Why Choose Us page saved!');
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setIsSaving(false);
    }
  };

  const updateStat = (index, field, value) => {
    const newStats = [...content.stats];
    newStats[index] = { ...newStats[index], [field]: value };
    setContent({ ...content, stats: newStats });
  };

  const updateBenefit = (index, field, value) => {
    const newBenefits = [...content.benefits];
    newBenefits[index] = { ...newBenefits[index], [field]: value };
    setContent({ ...content, benefits: newBenefits });
  };

  const updateGuarantee = (lang, index, value) => {
    const field = `guarantees_${lang}`;
    const newGuarantees = [...content[field]];
    newGuarantees[index] = value;
    setContent({ ...content, [field]: newGuarantees });
  };

  const updateGridItem = (index, field, value) => {
    const newItems = [...(content.grid_items || DEFAULT_GRID_ITEMS)];
    newItems[index] = { ...newItems[index], [field]: value };
    setContent({ ...content, grid_items: newItems });
  };

  const updateField = (field, value) => setContent(prev => ({ ...prev, [field]: value }));

  if (isLoading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-[#C41E3A]" /> Hero Section
          </h3>
          <Tabs value={activeLang} onValueChange={setActiveLang}>
            <TabsList className="grid w-full grid-cols-3 mb-4">
              {LANGUAGES.map(lang => (
                <TabsTrigger key={lang.code} value={lang.code} className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
                  {lang.flag} {lang.name}
                </TabsTrigger>
              ))}
            </TabsList>
            {LANGUAGES.map(lang => (
              <TabsContent key={lang.code} value={lang.code}>
                <div className={`p-4 rounded-2xl space-y-3 ${lang.bg}`}>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Subtitle</Label>
                      <Input value={content[`hero_subtitle_${lang.code}`] || ''} onChange={(e) => updateField(`hero_subtitle_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input value={content[`hero_title_${lang.code}`] || ''} onChange={(e) => updateField(`hero_title_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                    </div>
                  </div>
                  <div>
                    <Label>Title Highlight (colored part)</Label>
                    <Input value={content[`hero_title_highlight_${lang.code}`] || ''} onChange={(e) => updateField(`hero_title_highlight_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea value={content[`hero_description_${lang.code}`] || ''} onChange={(e) => updateField(`hero_description_${lang.code}`, e.target.value)} className="mt-1 bg-white" rows={2} />
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Stats */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#C41E3A]" /> Statistics
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {content.stats.map((stat, index) => (
              <div key={index} className="p-3 border rounded-xl bg-slate-50">
                <div className="mb-2">
                  <Label className="text-xs">Number</Label>
                  <Input value={stat.number} onChange={(e) => updateStat(index, 'number', e.target.value)} className="mt-1" />
                </div>
                <Tabs defaultValue="en" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 h-8">
                    {LANGUAGES.map(lang => (
                      <TabsTrigger key={lang.code} value={lang.code} className="text-xs py-1">{lang.flag}</TabsTrigger>
                    ))}
                  </TabsList>
                  {LANGUAGES.map(lang => (
                    <TabsContent key={lang.code} value={lang.code} className="mt-2">
                      <Input value={stat[`label_${lang.code}`] || ''} onChange={(e) => updateStat(index, `label_${lang.code}`, e.target.value)} placeholder={`Label (${lang.code.toUpperCase()})`} />
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Benefits */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#C41E3A]" /> Benefits / Advantages
          </h3>
          
          {/* Section Titles */}
          <Tabs defaultValue="en" className="w-full mb-4">
            <TabsList className="grid w-full grid-cols-3 mb-4">
              {LANGUAGES.map(lang => (
                <TabsTrigger key={lang.code} value={lang.code}>{lang.flag} {lang.name}</TabsTrigger>
              ))}
            </TabsList>
            {LANGUAGES.map(lang => (
              <TabsContent key={lang.code} value={lang.code}>
                <div className={`p-4 rounded-2xl space-y-3 ${lang.bg}`}>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Subtitle (small text)</Label>
                      <Input value={content[`benefits_subtitle_${lang.code}`] || ''} onChange={(e) => updateField(`benefits_subtitle_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input value={content[`benefits_title_${lang.code}`] || ''} onChange={(e) => updateField(`benefits_title_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                    </div>
                  </div>
                  <div>
                    <Label>Title Highlight (colored)</Label>
                    <Input value={content[`benefits_title_highlight_${lang.code}`] || ''} onChange={(e) => updateField(`benefits_title_highlight_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>

          {/* Individual Benefits */}
          <div className="space-y-4">
            {content.benefits.map((benefit, index) => (
              <div key={index} className="p-4 border rounded-xl bg-slate-50">
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Benefit #{index + 1}</Label>
                <Tabs defaultValue="en" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 h-8 mb-2">
                    {LANGUAGES.map(lang => (
                      <TabsTrigger key={lang.code} value={lang.code} className="text-xs py-1">{lang.flag}</TabsTrigger>
                    ))}
                  </TabsList>
                  {LANGUAGES.map(lang => (
                    <TabsContent key={lang.code} value={lang.code} className="space-y-2">
                      <Input value={benefit[`title_${lang.code}`] || ''} onChange={(e) => updateBenefit(index, `title_${lang.code}`, e.target.value)} placeholder={`Title (${lang.code.toUpperCase()})`} />
                      <Textarea value={benefit[`description_${lang.code}`] || ''} onChange={(e) => updateBenefit(index, `description_${lang.code}`, e.target.value)} placeholder={`Description (${lang.code.toUpperCase()})`} rows={2} />
                      <Input value={benefit[`highlight_${lang.code}`] || ''} onChange={(e) => updateBenefit(index, `highlight_${lang.code}`, e.target.value)} placeholder={`Highlight badge (${lang.code.toUpperCase()})`} />
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Guarantees */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-[#C41E3A]" /> Guarantees
          </h3>
          
          {/* Section Titles */}
          <Tabs defaultValue="en" className="w-full mb-4">
            <TabsList className="grid w-full grid-cols-3 mb-4">
              {LANGUAGES.map(lang => (
                <TabsTrigger key={lang.code} value={lang.code}>{lang.flag} {lang.name}</TabsTrigger>
              ))}
            </TabsList>
            {LANGUAGES.map(lang => (
              <TabsContent key={lang.code} value={lang.code}>
                <div className={`p-4 rounded-2xl space-y-3 ${lang.bg}`}>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Subtitle</Label>
                      <Input value={content[`guarantees_subtitle_${lang.code}`] || ''} onChange={(e) => updateField(`guarantees_subtitle_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input value={content[`guarantees_title_${lang.code}`] || ''} onChange={(e) => updateField(`guarantees_title_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                    </div>
                  </div>
                  <div>
                    <Label>Title Highlight</Label>
                    <Input value={content[`guarantees_title_highlight_${lang.code}`] || ''} onChange={(e) => updateField(`guarantees_title_highlight_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea value={content[`guarantees_text_${lang.code}`] || ''} onChange={(e) => updateField(`guarantees_text_${lang.code}`, e.target.value)} className="mt-1 bg-white" rows={2} />
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>

          {/* Guarantee List */}
          <h4 className="font-medium text-slate-700 mb-3">Guarantee Checkmarks</h4>
          {content.guarantees_en.map((_, index) => (
            <div key={index} className="mb-3 p-3 border rounded-xl bg-slate-50">
              <Label className="text-xs mb-2 block">Guarantee #{index + 1}</Label>
              <Tabs defaultValue="en" className="w-full">
                <TabsList className="grid w-full grid-cols-3 h-8">
                  {LANGUAGES.map(lang => (
                    <TabsTrigger key={lang.code} value={lang.code} className="text-xs py-1">{lang.flag}</TabsTrigger>
                  ))}
                </TabsList>
                {LANGUAGES.map(lang => (
                  <TabsContent key={lang.code} value={lang.code} className="mt-2">
                    <Input value={content[`guarantees_${lang.code}`][index] || ''} onChange={(e) => updateGuarantee(lang.code, index, e.target.value)} placeholder={`Guarantee text (${lang.code.toUpperCase()})`} />
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* CTA */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Call to Action Section</h3>
          <Tabs defaultValue="en" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-4">
              {LANGUAGES.map(lang => (
                <TabsTrigger key={lang.code} value={lang.code}>{lang.flag} {lang.name}</TabsTrigger>
              ))}
            </TabsList>
            {LANGUAGES.map(lang => (
              <TabsContent key={lang.code} value={lang.code}>
                <div className={`p-4 rounded-2xl space-y-3 ${lang.bg}`}>
                  <div>
                    <Label>CTA Title</Label>
                    <Input value={content[`cta_title_${lang.code}`] || ''} onChange={(e) => updateField(`cta_title_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                  </div>
                  <div>
                    <Label>CTA Text</Label>
                    <Textarea value={content[`cta_text_${lang.code}`] || ''} onChange={(e) => updateField(`cta_text_${lang.code}`, e.target.value)} className="mt-1 bg-white" rows={2} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Get Quote Button</Label>
                      <Input value={content[`get_quote_btn_${lang.code}`] || ''} onChange={(e) => updateField(`get_quote_btn_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                    </div>
                    <div>
                      <Label>Contact Us Button</Label>
                      <Input value={content[`contact_btn_${lang.code}`] || ''} onChange={(e) => updateField(`contact_btn_${lang.code}`, e.target.value)} className="mt-1 bg-white" />
                    </div>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Grid Items */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#C41E3A]" /> Feature Grid (4 boxes)
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {(content.grid_items || DEFAULT_GRID_ITEMS).map((item, index) => (
              <div key={index} className="p-3 border rounded-xl bg-slate-50">
                <Label className="text-sm font-medium mb-2 block">Box #{index + 1}</Label>
                <Tabs defaultValue="en" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 h-8">
                    {LANGUAGES.map(lang => (
                      <TabsTrigger key={lang.code} value={lang.code} className="text-xs py-1">{lang.flag}</TabsTrigger>
                    ))}
                  </TabsList>
                  {LANGUAGES.map(lang => (
                    <TabsContent key={lang.code} value={lang.code} className="space-y-2 mt-2">
                      <Input value={item[`title_${lang.code}`] || ''} onChange={(e) => updateGridItem(index, `title_${lang.code}`, e.target.value)} placeholder={`Title (${lang.code.toUpperCase()})`} />
                      <Input value={item[`sub_${lang.code}`] || ''} onChange={(e) => updateGridItem(index, `sub_${lang.code}`, e.target.value)} placeholder={`Subtitle (${lang.code.toUpperCase()})`} />
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={isSaving} className="w-full bg-[#C41E3A] hover:bg-[#A01830]">
        <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save Why Choose Us Page'}
      </Button>
    </div>
  );
}
